/**
 * Class for defining an input form
 */
import {FormModel} from "./form.model.abstract";
import {UntypedFormControl, UntypedFormGroup, ValidatorFn} from "@angular/forms";
import {FormModelCombine} from "./form.model.combine";
import {EventEmitter} from "@angular/core";
import ol from "ol/dist/ol";
import string = ol.string;

/**
 * Abstract class for holding configuration for a form. Must be extended and filled with fields and groups.
 */
export abstract class FormConfig {
  /**
   * List of fields for the current form. List of FormModel objects with the configuration for each field.
   * @protected
   */
  protected abstract fields: FormModel[];

  /**
   * List of validators for the whole form. Can be used to validate between fields.
   * @protected
   */
  protected formValidators: ValidatorFn[] = [];

  /**
   * List of formvalidators and which fields should be marked as invalid
   * @protected
   */
  protected formValidatorsFields: {[key: string]: string[]} = {};

  /**
   * Object with alle groups of the current form. Each group has a name and the list of fields in this group.
   * @protected
   */
  protected abstract groups: any;

  /**
   * The formgroup with all controls
   * @protected
   */
  protected form: UntypedFormGroup;

  /**
   * Can be used to add an additional postfix for all labels in the form.
   * @protected
   */
  public labelPostfix: string = null;

  /**
   * Can be used to add an additional postfix on the infobutton fields for easier fallbacks.
   * @protected
   */
  protected infoPostfix: string = null;

  /**
   * List of fields that use the infopostfix
   * @protected
   */
  protected infoPostfixFields: string[] = [] ;

  /**
   * Eventemitter when the form is submitted.
   */
  public submitted: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Eventemitter if the group changes. We do not use this in his base class. But it can be used in child classes.
   */
  public groupChange: EventEmitter<any> = new EventEmitter();

  /**
   * Eventemitter that is called when fields are changed, based on changes of data or validity
   */
  public fieldChange: EventEmitter<void> = new EventEmitter<void>();

  /**
   * timeout that is used to avoid calling event trigger to often
   */
  changeTimeout: any;

  /**
   * returns the list of all fields.
   */
  getFields(): FormModel[] {
    return this.fields;
  }

  /**
   * returns the list of group names in this form.
   */
  getGroupNames(): string[] {
    return Object.getOwnPropertyNames(this.groups);
  }

  /**
   * returns the list of fields in a specific group of this form.
   * @param groupName
   */
  getFieldsFromGroup(groupName: string): FormModel[] {
    if(this.groups.hasOwnProperty(groupName) === false) {
      // console.error("group does not exists: ", groupName);
      // this could happen because of a missconfiguration or a change in the groups
      return [];
    }

    // filter the existing fields for the ines that are configured in the group and sort them by the group order.
    return this.fields.filter(field => {
      return this.groups[groupName].indexOf(field.id) !== -1;
    }).sort((a:FormModel, b:FormModel) => {
      return this.groups[groupName].indexOf(a.id) > this.groups[groupName].indexOf(b.id)? 1 : -1
    });
  }

  /**
   * returns all feildnames of a group
   * @param groupName
   */
  getFieldnamesOfGroup(groupName: string): string[] {
    return this.groups.hasOwnProperty(groupName)? this.groups[groupName] : [];
  }

  /**
   * Returns the Formgroup object for the current config
   */
  getForm(groupName = null): UntypedFormGroup {
    if(!this.form) {
      this.form = new UntypedFormGroup({}, {updateOn: "change", validators: this.formValidators});

      const fields = groupName === null ? this.getFields() : this.getFieldsFromGroup(groupName);

      for (const field of fields) {
        this.addFieldToForm(this.form, field);

        if(field.isActive() === false) {
          this.deactivateField(field.id);
        }
      }

      // subscribe to changes on the form and call an event handler
      this.form.valueChanges.subscribe(() => {
        if(this.changeTimeout) {
          clearTimeout(this.changeTimeout);
        }
        // in case of data setting of several fields at once this can lead to a long call stack. Use a timeout here.
        this.changeTimeout = setTimeout(() => this.onChange(), 50);
      });
    }
    return this.form;
  }

  /**
   * adds a formModel to a formGroup. Depending on the type of form, there has to be some logic
   * @param form
   * @param field
   * @param emitEvent
   */
  public addFieldToForm(form: UntypedFormGroup, field: FormModel, emitEvent: boolean = true): void {
    // add validators and preset values
    switch (field.type) {
      case "dates":
      case "percent":
        this.form.addControl(field.fieldData.ids[0],
          new UntypedFormControl(field.fieldData.values.length > 0 ? field.fieldData.values[0] : null, field.getValidators()), {emitEvent});
        this.form.addControl(field.fieldData.ids[1],
          new UntypedFormControl(field.fieldData.values.length > 1 ? field.fieldData.values[1] : null, field.getValidators()), {emitEvent});
        break;
      case "combine":
        for (const subfield of (field as FormModelCombine).getFields()) {
          this.form.addControl(subfield.id, new UntypedFormControl(subfield.getValue(), subfield.getValidators()), {emitEvent});
          if(subfield.type === 'textselect') {
            this.form.addControl(subfield.fieldData.secondId, new UntypedFormControl(subfield.getOptions()[0], []), {emitEvent});
          }
        }
        break;
      case "textselect":
        this.form.addControl(field.id, new UntypedFormControl(field.getValue(), field.getValidators()));
        this.form.addControl(field.fieldData.secondId, new UntypedFormControl(field.getOptions()[0], []), {emitEvent});
        break;
      case "doubletext-select":
        this.form.addControl(field.id, new UntypedFormControl(field.getValue(), field.getValidators()));
        this.form.addControl(field.fieldData.secondId, new UntypedFormControl(field.getValue(), field.getValidators()));
        this.form.addControl(field.fieldData.selectId, new UntypedFormControl(field.getOptions()[0], []), {emitEvent});
        break;
      case 'checkboxes':
        const id = field.id;
        field.getIds().forEach((id, index) => {
          this.form.addControl(id, new UntypedFormControl(field.fieldData.values?.length > index ? field.fieldData.values[index] : null,
            field.getValidators()), {emitEvent});
        });
        break;
      case "direction":
        this.form.addControl(field.id, new UntypedFormControl(field.getValue(), field.getValidators()), {emitEvent});
        this.form.addControl(field.fieldData.secondId, new UntypedFormControl('', []));
        break;
      default:
        this.form.addControl(field.id, new UntypedFormControl(field.getValue(), field.getValidators()), {emitEvent});
        break;
    }

  }

  /**
   * Returns a single field from this.fields
   * @param id The ID of the field
   */
  getField(id: string): FormModel {
    const field = this.fields.find(field => field.getIds().indexOf(id) !== -1 || field.id === id);
    if(field && field.id === id) {
      return field;
    }
    else if(field) {
      switch (field.type) {
        case "combine":
          return (<FormModelCombine>field).getFields().find(field => field.id === id);
      }
    }

    return field;
  }

  /**
   * Eventhandler that should be called when the form is submitted
   */
  onSubmit(): boolean {
    this.resetFormValidatorsFromFields();

    // remove serverErrors on every field before submit
    for(const field of Object.keys(this.form.controls)) {
      if((<UntypedFormControl>this.form.controls[field]).hasError('serverError')) {
        (<UntypedFormControl>this.form.controls[field]).setErrors({'serverError': null});
        (<UntypedFormControl>this.form.controls[field]).updateValueAndValidity();
      }
    }

    if(this.form.valid === false) {
      // debug output of all invalid fields
      console.debug("Form is invalid", this.form.errors);

      // mark all fiels of formvalidators as invalid if they are defined.
      this.invalidateFieldsOfFormvalidators(this.form.errors);

      // mark all invalid fields as touched, so we see the error message.
      for(const field in this.form.controls) {
        if(this.form.controls[field].valid === false) {
          this.form.controls[field].markAsTouched({onlySelf: true});
          console.debug(field, this.form.controls[field].errors);
        }
      }
    }

    this.submitted.emit(this.form.valid);
    return this.form.valid;
  }

  /**
   * Set on some fields the invalid state manually, if there was a form validator fired
   * @param formErrors
   */
  invalidateFieldsOfFormvalidators(formErrors: any): void {
    Object.keys(formErrors).forEach(formError => {
      if(this.formValidatorsFields.hasOwnProperty(formError)) {
        this.formValidatorsFields[formError].forEach(field => {
          (<UntypedFormControl>this.form.controls[field]).setErrors({'formValidator': true});
          (<UntypedFormControl>this.form.controls[field]).markAsTouched({onlySelf: true});
        });
      }
    });
  }

  /**
   * if errors should be reseeted, als all formvalidator errors must be removed.
   */
  resetFormValidatorsFromFields(): void {
    for(const field of Object.keys(this.form.controls)) {
      if((<UntypedFormControl>this.form.controls[field]).hasError('formValidator')) {
        (<UntypedFormControl>this.form.controls[field]).setErrors({'formValidator': null});
        (<UntypedFormControl>this.form.controls[field]).updateValueAndValidity();
      }
    }
  }

  /**
   * can be implemented by child classes to do changes on the form when there is a change (selection of value, validation changed, etc.)
   * If you do changes, call this.fieldChange emitter
   * @protected
   */
  protected onChange(): void {}

  /**
   * Hides a field in the form.
   *
   * @param field
   */
  public hideField(field: FormModel): void {
    this.deactivateField(field.id);
  }

  /**
   * Sets the additional label Postfix.
   * @param postfix
   */
  public setPostfix(postfix: string): void {
    this.setLabelPostfix(postfix);
    this.setInfoPostfix(postfix);
  }

  /**
   * set label postfix
   * @param postfix
   */
  public setLabelPostfix(postfix: string): void {
    // an empty postfix (e.g. the heizcheck photo step presets messdienstleister to '') would build
    // "textkey|", which AppTranslatePipe cannot strip, so the raw textkey would be shown
    this.labelPostfix = postfix ? '|' + postfix : null;
  }

  /**
   * Set the infopostfix for a list of fields
   * @param postfix
   * @param fields
   */
  public setInfoPostfix(postfix: string, fields: string[] = []): void {
    this.infoPostfix = postfix ? '_' + postfix : null;
    this.infoPostfixFields = fields;
  }

  /**
   * Returns the postfix for the label textkeys
   */
  public getLabelPostfix(): string {
    return this.labelPostfix === null ? '' : this.labelPostfix;
  }

  /**
   * Returns the postfix for the infobutton fieldnames
   */
  public getInfoPostfix(fieldId: string = null): string {
    const usePostfix = this.infoPostfixFields.length === 0 || this.infoPostfixFields.includes(fieldId);
    return this.infoPostfix === null || usePostfix === false ? '' : this.infoPostfix;
  }

  /**
   * Returns a label textkey.
   * @param step
   * @param id
   */
  getLabel(step: string, id: string): string {
    return step + "." + id + ".label" + this.getLabelPostfix();
  }

  /**
   * Activates a field (activate validation) and show it.
   * @param id
   * @protected
   */
  protected activateField(id: string): void {
    const field = this.getField(id);

    field.setActive();

    // activate all (sub)fields in the form object
    field.getIds().map(fieldId => {
      if(this.form.controls[fieldId] && this.form.controls[fieldId].enabled === false) {
        this.form.controls[fieldId].enable({onlySelf: false, emitEvent: true});
      }
    });
  }

  /**
   * Deactivate a field (remove validation) and hide it.
   * @param id
   * @protected
   */
  protected deactivateField(id: string): void {
    const field = this.getField(id);

    field.setInactive();

    // deactivate all (sub)fields in the form object
    field.getIds().map(fieldId => {
      if(this.form.controls[fieldId] && this.form.controls[fieldId].disabled === false) {
        this.form.controls[fieldId].disable({onlySelf: false, emitEvent: true});
      }
    });
  }

  /**
   * Disable a subfield
   * @param id
   * @protected
   */
  protected disableSubfield(id: string): void {
    const field = this.getField(id);
    if (field) {
      field.getIds().map(fieldId => {
        if (fieldId === id && this.form.controls[fieldId] && this.form.controls[fieldId].disabled === false) {
          this.form.controls[fieldId].disable({onlySelf: false, emitEvent: true});
        }
      });
    }
  }

  /**
   * Enable a subfield
   * @param id
   * @protected
   */
  protected enableSubfield(id: string): void {
    const field = this.getField(id);
    if (field) {
      field.getIds().map(fieldId => {
        if (fieldId === id && this.form.controls[fieldId] && this.form.controls[fieldId].enabled === false) {
          this.form.controls[fieldId].enable({onlySelf: false, emitEvent: true});
        }
      });
    }
  }

  /**
   * helper: Returns the first day of the last year
   *
   @param offset extra ofset of the year calculation, default is last year, could be extended with more years here
   */
  public static getFirstDayOfLastYear(offset: number = 0): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() - (1 + offset));
    date.setMonth(0);
    date.setDate(1);

    return date.toLocaleDateString('de-DE');
  }

  /**
   * helper: Returns the last day of the last year
   *
   * @param offset extra ofset of the year calculation, default is last year, could be extended with more years here
   */
  public static getLastDayOfLastYear(offset: number = 0): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() - (1 + offset));
    date.setMonth(11);
    date.setDate(31);

    return date.toLocaleDateString('de-DE');
  }

  /**
   * Sets data on the form. Receives a list of fields and theire value
   * @param values
   */
  public setData(values: {[key: string]: any}): void {
    for(const fieldName of Object.keys(values)) {
      if(this.form?.controls[fieldName] && typeof values[fieldName] !== "undefined" && values[fieldName] !== null) {
        let value = values[fieldName].hasOwnProperty('value') ? values[fieldName].value : values[fieldName];

        // get field data
        let field = this.getField(fieldName);

        // check if the field ends with a digit and is not in form config, remove the digit
        if(!field && fieldName.match(/[0-9]+$/)) {
          field = this.getField(fieldName.replace(/[0-9]+$/, ''));
        }

        // handle number values
        if(value && field?.fieldData?.fieldType === 'number') {
          // make sure we have a string
          value += "";
          // replace dot with comma
          value = value.replace(/\./, ",");
        }

        this.form.controls[fieldName].setValue(value);
      }
    }

    // call trigger if data is changed
    this.onSetData(values);

    if(this.form) {
      this.form.updateValueAndValidity({onlySelf: false, emitEvent: true});
    }
  }

  /**
   * Event that is fired, every time data is set to the form.
   * If you do changes, emit this.fieldChange
   * @param values
   * @protected
   */
  protected onSetData(values: {[key: string]: any}): void {}

  /**
   * Retruns all data from the form. Some datatapyes like datas are converted into strings.
   */
  public getData(): {[key: string]: any} {
    const values = {};
    for(const fieldName of Object.keys(this.form.value)) {
      let field = this.getField(fieldName);

      // check if the field ends with a digit and is not in form config, remove the digit
      if(!field && fieldName.match(/[0-9]+$/)) {
        field = this.getField(fieldName.replace(/[0-9]+$/, ''));
      }

      if(field?.fieldData?.fieldType === 'number' ||
        (typeof this.form.value[fieldName] === "string" && /^[1-9][0-9,]*$/.test(this.form.value[fieldName]))) {
        // replace comma with dot
        if (typeof this.form.value[fieldName] === "string" && this.form.value[fieldName]?.includes(',')) {
          this.form.value[fieldName] = this.form.value[fieldName].replace(/,/, '.');
        }

        // try to map inputs on number values if possible.
        values[fieldName] = isNaN(this.form.value[fieldName]) || this.form.value[fieldName]?.length === 0 ?
          this.form.value[fieldName] : this.form.value[fieldName] * 1;
      }
      else {
        // in some cases we do not have a field in the formconfig for a field in the form (i.e. for dynamic fields)
        values[fieldName] = this.form.value[fieldName];
      }
    }
    return values;
  }

  /**
   * set/change the validators of a specific field
   * @param id
   * @param validators
   */
  setFieldValidators(id: string, validators: ValidatorFn[]): void {
    // save the config in the fieldconfig
    const fieldConfig = this.getField(id);
    fieldConfig.setValidators(validators);

    // update validators on the current form, if the field is present.
    const field = this.form.get(id);
    if(field) {
      field.setValidators(fieldConfig.getValidators());
      field.updateValueAndValidity({onlySelf: true});
    }
  }

  /**
   * Change the options for a specific field.
   * @param id
   * @param options
   */
  setOptions(id: string, options: string[]): void {
    const fieldConfig = this.getField(id);
    fieldConfig.setOptions(options);
  }


}
